import { FILE_CHUNK_SIZE_IN_BYTES } from 'lib/constants/s3FileUpload';
import { IFetchResponse } from 'lib/requests/fetch';
import {
  IS3FileMultipartUploadPartWithEtag,
  IS3FileMultipartUploadPartWithPresignedUrl,
} from 'lib/types/s3-files';
import _ from 'lodash';
import { GuessedFile } from 'magic-bytes.js/dist/model/tree';
import { useCallback, useEffect, useState } from 'react';
import { s3PresignedUrlUpload } from './api';
import { ABORT_ERROR_NAME } from './constants';
import { getNumberOfParts, validateFileType } from './helpers';

const SIMULTANEOUS_REQUEST_COUNT = 6;
const ETAG_HEADER_NAME = 'etag';

export interface ICompleteMultipartUploadResponse<T> {
  file: File;
  // TODO (mitesh): Remove `fileId` in favor of getting it from the `response` itself.
  // This can be removed after the old content creation stuff is torn down.
  /**
   * @deprecated
   */
  fileId: number;
  guessedFile: GuessedFile;
  response: T;
}

export interface IS3UploadAdapter {
  createMultipartUploadAndTranslateResponse: (payload: unknown) => Promise<{
    entityId: number;
    parts: IS3FileMultipartUploadPartWithPresignedUrl[];
    uploadId: string;
  }>;
  completeMultipartUpload: (
    payload: unknown
  ) => Promise<IFetchResponse<unknown>>;
  buildCreatePayload: (props: {
    file: File;
    guessedFile: GuessedFile;
  }) => Promise<unknown>;
  buildCompletePayload: (props: {
    entityId: number;
    parts: IS3FileMultipartUploadPartWithEtag[];
    uploadId: string;
  }) => unknown;
}

interface IUseS3UploaderProps {
  accept: string;
  adapter: IS3UploadAdapter;
  onProgress: (percentComplete: number) => void;
}

interface IUseS3UploaderReturn {
  abortUpload: () => void;
  percentComplete: number;
  uploadFile: (
    file: File
  ) => Promise<ICompleteMultipartUploadResponse<unknown>>;
}

export const useS3Uploader = ({
  accept,
  adapter,
  onProgress,
}: IUseS3UploaderProps): IUseS3UploaderReturn => {
  const [controller, setController] = useState<AbortController>(
    new AbortController()
  );
  const [fileKey, setFileKey] = useState<number>(0);
  const [percentComplete, setPercentComplete] = useState<number>(0);

  useEffect(() => () => controller.abort(), []);

  useEffect(() => {
    onProgress(percentComplete);
  }, [percentComplete]);

  useEffect(() => {
    setPercentComplete(0);
  }, [fileKey]);

  const uploadFile = useCallback(
    async (file: File): Promise<ICompleteMultipartUploadResponse<unknown>> => {
      const uploadSinglePart = async (
        part: IS3FileMultipartUploadPartWithPresignedUrl
      ): Promise<Response> => {
        const numberOfParts = getNumberOfParts(file.size);
        const start = (part.partNumber - 1) * FILE_CHUNK_SIZE_IN_BYTES;
        const end = part.partNumber * FILE_CHUNK_SIZE_IN_BYTES;
        const blob =
          part.partNumber < numberOfParts
            ? file.slice(start, end)
            : file.slice(start);

        return s3PresignedUrlUpload({
          url: part.presignedUrl,
          blob,
          onSuccess: () => onUploadPartSuccess(),
          options: {
            signal: controller.signal,
          },
        });
      };

      const uploadParts = async (
        partsWithUrls: IS3FileMultipartUploadPartWithPresignedUrl[]
      ): Promise<IS3FileMultipartUploadPartWithEtag[]> => {
        const partsChunks = _.chunk(partsWithUrls, SIMULTANEOUS_REQUEST_COUNT);
        const partsWithEtags: IS3FileMultipartUploadPartWithEtag[] = [];

        for (
          let chunkIndex = 0;
          chunkIndex < _.size(partsChunks);
          chunkIndex++
        ) {
          const chunk = partsChunks.at(chunkIndex);
          const responses = await Promise.all(_.map(chunk, uploadSinglePart));
          const partsWithEtagsChunk = _.map(responses, (part, partIndex) => ({
            etag: part.headers.get(ETAG_HEADER_NAME),
            partNumber: chunkIndex * SIMULTANEOUS_REQUEST_COUNT + partIndex + 1,
          }));
          partsWithEtags.push(...partsWithEtagsChunk);
        }

        return partsWithEtags;
      };

      const onUploadPartSuccess = () => {
        const numberOfParts = getNumberOfParts(file.size);
        const percentOfPart = _.round(100 / numberOfParts);

        setPercentComplete((prev) => {
          const newPercent = prev + percentOfPart;
          return newPercent > 100 ? 100 : newPercent;
        });
      };

      // We use the `fileKey` to "track" the upload. If the file changes, we
      // update the key, and respond as necessary (to reset the internal
      // progress state, for example).
      setFileKey((prev) => prev + 1);

      const { guessedFile, isValid } = await validateFileType(accept, file);
      if (!isValid) {
        return;
      }

      const createPayload = await adapter.buildCreatePayload({
        file,
        guessedFile,
      });
      const createRes =
        await adapter.createMultipartUploadAndTranslateResponse(createPayload);
      let partsWithEtags: IS3FileMultipartUploadPartWithEtag[] = [];

      try {
        partsWithEtags = await uploadParts(createRes.parts);
      } catch (error) {
        if (error.name === ABORT_ERROR_NAME) {
          setController(new AbortController());
          throw error;
        }
      }

      const completePayload = adapter.buildCompletePayload({
        parts: partsWithEtags,
        uploadId: createRes.uploadId,
        entityId: createRes.entityId,
      });
      const completeRes =
        await adapter.completeMultipartUpload(completePayload);
      if (completeRes.error) {
        throw new Error(completeRes.message);
      }

      return {
        file,
        fileId: createRes.entityId,
        guessedFile,
        response: completeRes.data,
      };
    },
    [accept, adapter, controller]
  );

  return {
    abortUpload: () => controller.abort(),
    percentComplete,
    uploadFile,
  };
};
